import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './DashboardAtleta.css';
import EditProfileModal from '../../components/EditProfileModal/EditProfileModal';
import TeamDetailsModal from '../../components/TeamDetailsModal/TeamDetailsModal';
import CalendarModal from '../../components/CalendarModal/CalendarModal';
import GameDetailModal from '../../components/Game/GameDetailModal';
import PaymentCard from '../../components/Payment/PaymentCard';
import RequestsModal from '../../components/RequestsModal/RequestsModal';
import DocumentsModal from '../../components/DocumentsModal/DocumentsModal';
import MembershipCard, { getCardPaymentState } from '../../components/MembershipCard/MembershipCard';
import ReceiptsModal from '../../components/ReceiptsModal/ReceiptsModal';
import NotificationsModal from '../../components/NotificationsModal/NotificationsModal';
import TicketsModal from '../../components/TicketsModal/TicketsModal';
import AdminPinModal from '../../components/AdminPinModal/AdminPinModal';
import ConsentRequestModal from '../../components/ConsentRequestModal/ConsentRequestModal';
import FamilyPrivacyModal from '../../components/FamilyPrivacyModal/FamilyPrivacyModal';
import { FaLock, FaShieldAlt } from 'react-icons/fa';
import { getSportConfig } from '../../config/sportStats';
import API_BASE_URL from '../../config/api';

const DashboardAtleta = () => {
    const navigate = useNavigate();
    const userRoles = (() => { try { return JSON.parse(localStorage.getItem('roles') || '[]').map(r => r.toLowerCase()); } catch { return []; } })();
    const isAlsoTreinador = userRoles.includes('treinador');
    const isAlsoCoordenador = userRoles.includes('coordenador') || userRoles.includes('coordinator');
    const location = useLocation();
    const [athleteData, setAthleteData] = useState(null);
    const [teamsData, setTeamsData] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [events, setEvents] = useState([]);
    const [athleteMetrics, setAthleteMetrics] = useState({ gamesPlayed: '-', attendanceRate: '-' });
    const [teamReport, setTeamReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingTab, setLoadingTab] = useState(false);
    const [error, setError] = useState(null);
    const [paymentToast, setPaymentToast] = useState(null); // 'success' | 'cancelled' | null
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isRequestsModalOpen, setIsRequestsModalOpen] = useState(false);
    const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
    const [isReceiptsModalOpen, setIsReceiptsModalOpen] = useState(false);
    const [quotaBreakdown, setQuotaBreakdown] = useState([]);
    const [discountsApplied, setDiscountsApplied] = useState([]);
    const [inscriptionInfo, setInscriptionInfo] = useState([]);
    const [parentsPaymentWarning, setParentsPaymentWarning] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
    const [expandedNotificationId, setExpandedNotificationId] = useState(null);
    const [isAllNotificationsModalOpen, setIsAllNotificationsModalOpen] = useState(false);
    const [annualPasses, setAnnualPasses] = useState([]);
    const [isGameDetailOpen, setIsGameDetailOpen] = useState(false);
    const [isTicketsModalOpen, setIsTicketsModalOpen] = useState(false);
    const [showAdminPinModal, setShowAdminPinModal] = useState(false);
    const [pendingSwitchUser, setPendingSwitchUser] = useState(null);
    const [selectedGameForDetail, setSelectedGameForDetail] = useState(null);



    // Linked users — refreshed from the API on every mount so newly-accepted
    // family association requests become visible without forcing a re-login.
    const [linkedUsers, setLinkedUsers] = useState(() => {
        try {
            const stored = localStorage.getItem('linkedUsers');
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });
    const [selectedUserId, setSelectedUserId] = useState(() => {
        return parseInt(localStorage.getItem('userId')) || null;
    });
    const [isSwitchingUser, setIsSwitchingUser] = useState(false);
    const [switchingToName, setSwitchingToName] = useState('');

    const refreshLinkedUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const primaryUserId = localStorage.getItem('primaryUserId');
            if (!token) return;
            const res = await fetch(`${API_BASE_URL}/api/auth/linked-users?primaryUserId=${primaryUserId || ''}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Primary-User-Id': primaryUserId || ''
                }
            });
            if (res.ok) {
                const data = await res.json();
                setLinkedUsers(data);
                if (data.length > 0) {
                    localStorage.setItem('linkedUsers', JSON.stringify(data));
                } else {
                    localStorage.removeItem('linkedUsers');
                }
            }
        } catch (err) {
            console.warn('Could not refresh linked users:', err);
        }
    };

    // Refresh linked users from server on mount
    useEffect(() => {
        refreshLinkedUsers();
    }, []);

    const [refreshKey, setRefreshKey] = useState(0);
    const [isPollingPayment, setIsPollingPayment] = useState(false);

    // Detect ?payment=success / ?payment=cancelled after Stripe redirect
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paymentParam = params.get('payment');
        const redirectStatus = params.get('redirect_status');

        if (paymentParam === 'success') {
            if (redirectStatus === 'failed') {
                setPaymentToast('cancelled');
                setIsPollingPayment(false);
                window.history.replaceState({}, '', location.pathname);
                const timer = setTimeout(() => setPaymentToast(null), 6000);
                return () => clearTimeout(timer);
            } else {
                setIsPollingPayment(true);
                window.history.replaceState({}, '', location.pathname);
            }
        } else if (paymentParam === 'cancelled') {
            setPaymentToast('cancelled');
            window.history.replaceState({}, '', location.pathname);
            const timer = setTimeout(() => setPaymentToast(null), 6000);
            return () => clearTimeout(timer);
        }
    }, [location.search]);

    // Background polling for payment status completion
    useEffect(() => {
        if (!isPollingPayment || !selectedUserId) return;

        let attempt = 0;
        const maxAttempts = 10;

        const interval = setInterval(async () => {
            attempt++;
            if (attempt >= maxAttempts) {
                setIsPollingPayment(false);
                clearInterval(interval);
                return;
            }

            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/api/payment/quota?userId=${selectedUserId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const quotaData = await response.json();
                    if (quotaData.status === 'Regularizada' || (quotaData.totalDue ?? 0) === 0) {
                        setRefreshKey(prev => prev + 1);
                        setIsPollingPayment(false);
                        clearInterval(interval);
                    }
                }
            } catch (err) {
                console.error("Payment status polling error:", err);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [isPollingPayment, selectedUserId]);

    const [paymentReference, setPaymentReference] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState('unpaid');
    const [generatingReference, setGeneratingReference] = useState(false);
    const [startingPayment, setStartingPayment] = useState(false);
    const [quotaAmount, setQuotaAmount] = useState(null);
    const [totalDue, setTotalDue] = useState(null);
    const [overdueMonths, setOverdueMonths] = useState([]);
    const [futureMonths, setFutureMonths] = useState([]);
    const [paymentPreference, setPaymentPreference] = useState('Monthly');
    const [nextPeriod, setNextPeriod] = useState(null); // { month, year }
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [historyInscriptions, setHistoryInscriptions] = useState([]);
    const [historyYear, setHistoryYear] = useState(new Date().getFullYear());
    const userSelectedHistoryYearRef = useRef(false);

    const handleHistoryYearChange = (year) => {
        userSelectedHistoryYearRef.current = true;
        setHistoryYear(year);
    };
    const [isExemptFromGlobalFee, setIsExemptFromGlobalFee] = useState(false);

    // Start Stripe Checkout for quota payment
    const handleStartPayment = async (selectedOverdueMonths = null, includesCurrentMonth = true, selectedAmount = null, selectedFutureMonths = [], currentMonthItem = null) => {
        const token = localStorage.getItem('token');
        if (!token) { navigate('/login'); return; }

        const isAnnual = paymentPreference === 'Annual';

        // Effective overdue: what was selected in the modal (or all if called without modal)
        // Future months are merged in — they're just additional periods from the backend's perspective
        const effectiveOverdue = [
            ...(selectedOverdueMonths !== null ? selectedOverdueMonths : overdueMonths),
            ...(selectedFutureMonths || [])
        ];
        const effectiveAmount = selectedAmount !== null
            ? selectedAmount
            : (overdueMonths.length > 0 ? totalDue : quotaAmount);

        // Current period (only included when the user checked it in the modal)
        const finalMonth = includesCurrentMonth
            ? (isAnnual ? null : (new Date().getMonth() + 1))
            : null;
        const finalYear = includesCurrentMonth
            ? new Date().getFullYear()
            : null;

        let description = 'Quota';
        const totalMonths = effectiveOverdue.length + (includesCurrentMonth ? 1 : 0);
        if (totalMonths > 0) {
            if (effectiveOverdue.length > 0 && !includesCurrentMonth) {
                description = `Quotas — ${effectiveOverdue.length} ${effectiveOverdue.length === 1 ? 'mês' : 'meses'}`;
            } else if (effectiveOverdue.length > 0) {
                description = `Quotas — ${totalMonths} ${totalMonths === 1 ? 'mês' : 'meses'}`;
            } else if (finalYear) {
                if (isAnnual && finalMonth) {
                    const startMonthName = new Date(finalYear, finalMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long' });
                    const endMonth = finalMonth === 1 ? 12 : finalMonth - 1;
                    const endYear = finalMonth === 1 ? finalYear : finalYear + 1;
                    const endMonthName = new Date(endYear, endMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long' });
                    description = `Quota Anual — ${startMonthName.charAt(0).toUpperCase() + startMonthName.slice(1)} de ${finalYear} a ${endMonthName.charAt(0).toUpperCase() + endMonthName.slice(1)} de ${endYear}`;
                } else {
                    description = (!isAnnual && finalMonth)
                        ? `Quota Mensal — ${new Date(finalYear, (finalMonth - 1)).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}`
                        : `Quota Anual — ${finalYear}`;
                }
            }
        }

        navigate('/checkout', {
            state: {
                type: 'quota',
                amount: effectiveAmount,
                description,
                selectedUserId,
                buyerEmail: athleteData?.email || '',
                periodMonth: finalMonth,
                periodYear: finalYear,
                overdueMonths: effectiveOverdue,
                breakdown: currentMonthItem?.breakdown || quotaBreakdown,
                inscriptionInfo: currentMonthItem?.inscriptionInfo || inscriptionInfo,
                currentMonthDetails: currentMonthItem,
                isAnnual,
                successPath: '/dashboard-atleta',
                cancelPath: '/dashboard-atleta',
                queryParams: '?payment=success',
            }
        });
    };




    const handleBuyTicket = async (event) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/tickets/checkout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    eventId: event.id,
                    buyerUserId: selectedUserId,
                    buyerEmail: athleteData.email || athleteData.athleteProfile?.email,
                    buyerName: athleteData.athleteProfile
                        ? `${athleteData.athleteProfile.firstName || ''} ${athleteData.athleteProfile.lastName || ''}`.trim() || athleteData.fullName
                        : athleteData.fullName,
                    successUrl: window.location.origin + '/payment-success',
                    cancelUrl: window.location.origin + '/payment-cancel'
                })
            });

            if (!response.ok) throw new Error('Erro ao criar sessão de pagamento');

            const { url } = await response.json();

            if (url) {
                window.location.href = url;
            } else {
                throw new Error('URL de pagamento não recebida.');
            }
        } catch (err) {
            console.error(err);
            alert(err.message);
        }
    };

    const [showConsentModal, setShowConsentModal] = useState(false);
    const [targetConsentUser, setTargetConsentUser] = useState(null);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

    const checkPaymentStatus = async () => {
        window.location.reload();
    };

    const handleLinkedTabClick = async (lu) => {
        if (selectedUserId === lu.id) return;

        if (lu.requiresConsent) {
            setTargetConsentUser(lu);
            setShowConsentModal(true);
            return;
        }

        const type = lu.dashboardType?.toLowerCase() || 'socio';
        if (type === 'admin' || lu.requiresPin) {
            setPendingSwitchUser(lu);
            setShowAdminPinModal(true);
            return;
        }

        executeUserSwitch(lu);
    };

    const executeUserSwitch = async (lu) => {
        setIsSwitchingUser(true);
        setSwitchingToName(`${lu.firstName} ${lu.lastName}`);
        try {
            const token = localStorage.getItem('token');
            const primaryUserId = localStorage.getItem('primaryUserId');
            const res = await fetch(`${API_BASE_URL}/api/auth/switch-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-Primary-User-Id': primaryUserId || ''
                },
                body: JSON.stringify({ targetUserId: lu.id, primaryUserId: primaryUserId ? parseInt(primaryUserId) : null })
            });

            if (res.ok) {
                const data = await res.json();
                localStorage.setItem('token', data.token);
                localStorage.setItem('userId', data.id.toString());
                localStorage.setItem('roles', JSON.stringify(data.roles));
                localStorage.setItem('userName', `${data.firstName} ${data.lastName}`);
                localStorage.setItem('userEmail', data.email);

                const dashboardRoutes = {
                    atleta: '/dashboard-atleta',
                    treinador: '/dashboard-treinador',
                    coordenador: '/dashboard-coordenador',
                    socio: '/dashboard-socio',
                    admin: '/dashboard-admin',
                    user: '/dashboard-socio'
                };
                const type = lu.dashboardType?.toLowerCase() || 'socio';
                const route = dashboardRoutes[type] || '/dashboard-socio';

                if (route !== '/dashboard-atleta') {
                    navigate(route);
                    window.location.reload();
                } else {
                    setSelectedUserId(data.id);
                    window.location.reload();
                }
            } else {
                const err = await res.json().catch(() => ({}));
                alert(err.message || 'Erro ao alterar perfil');
                setIsSwitchingUser(false);
            }
        } catch (err) {
            console.error('Error switching user:', err);
            setIsSwitchingUser(false);
        }
    };

    // Fetch team report (includes both athlete metrics and team stats)
    useEffect(() => {
        const fetchTeamReport = async () => {
            if (!selectedTeamId || !athleteData?.athleteProfile?.id) return;
            const token = localStorage.getItem('token');
            try {
                const response = await fetch(`${API_BASE_URL}/api/teams/${selectedTeamId}/report`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setTeamReport(data);

                    const athleteId = athleteData.athleteProfile.id;
                    const gamesInfo = data.athleteStats?.find(a => a.athleteId === athleteId);
                    const attInfo = data.attendance?.athleteAttendance?.find(a => a.athleteId === athleteId);

                    setAthleteMetrics({
                        gamesPlayed: gamesInfo?.gamesPlayed || 0,
                        attendanceRate: attInfo?.rate || 0
                    });
                }
            } catch (err) {
                console.error("Error fetching team report:", err);
            }
        };
        fetchTeamReport();
    }, [selectedTeamId, athleteData]);

    // Fetch all data (User + Team + Events + Quota) whenever the selected tab (userId) changes
    useEffect(() => {
        if (!selectedUserId) return;

        const fetchData = async () => {
            if (!athleteData) setLoading(true);
            else setLoadingTab(true);

            try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('User not authenticated');

                // 1. Fetch User Profile
                const userResponse = await fetch(`${API_BASE_URL}/api/users/${selectedUserId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!userResponse.ok) throw new Error('Failed to fetch athlete data');
                const userData = await userResponse.json();
                console.log(userData);
                setAthleteData(userData);

                // 2. Fetch Quota for this user
                try {
                    const quotaResponse = await fetch(`${API_BASE_URL}/api/payment/quota?userId=${selectedUserId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    console.log(quotaResponse);
                    if (quotaResponse.ok) {
                        const quotaData = await quotaResponse.json();
                        console.log(quotaData);
                        setQuotaAmount(quotaData.amount);
                        setPaymentStatus(quotaData.status);
                        setPaymentReference(quotaData.existingPayment || null);
                        setPaymentPreference(quotaData.paymentPreference || 'Monthly');
                        // NEW: breakdown and discounts
                        setQuotaBreakdown(quotaData.breakdown || []);
                        setDiscountsApplied(quotaData.discountsApplied || []);
                        setInscriptionInfo(quotaData.inscriptionInfo || []);
                        setOverdueMonths(quotaData.overdueMonths || []);
                        setFutureMonths(quotaData.futureMonths || []);
                        setTotalDue(quotaData.totalDue ?? null);
                        setParentsPaymentWarning(quotaData.parentsPaymentWarning || false);
                        if (quotaData.nextPeriodYear) {
                            setNextPeriod({ month: quotaData.nextPeriodMonth, year: quotaData.nextPeriodYear });
                            if (!userSelectedHistoryYearRef.current && quotaData.nextPeriodYear > new Date().getFullYear()) {
                                setHistoryYear(quotaData.nextPeriodYear);
                            }
                        }
                    } else {
                        setQuotaAmount(0);
                        setPaymentStatus('-');
                        setPaymentReference(null);
                        setQuotaBreakdown([]);
                        setDiscountsApplied([]);
                        setInscriptionInfo([]);
                        setOverdueMonths([]);
                        setFutureMonths([]);
                        setParentsPaymentWarning(false);
                    }
                } catch (qErr) {
                    console.error('Error fetching quota:', qErr);
                }

                // 3b. Fetch Payment History
                try {
                    const currentCalMonth = new Date().getMonth() + 1;
                    const historyResponse = await fetch(`${API_BASE_URL}/api/payment/history?userId=${selectedUserId}&year=${historyYear}&month=${currentCalMonth}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (historyResponse.ok) {
                        const historyData = await historyResponse.json();
                        setPaymentHistory(Array.isArray(historyData) ? historyData : (historyData.payments || []));
                        setHistoryInscriptions(Array.isArray(historyData) ? [] : (historyData.inscriptions || []));
                    } else {
                        setPaymentHistory([]);
                        setHistoryInscriptions([]);
                    }
                } catch (hErr) {
                    console.error('Error fetching payment history:', hErr);
                    setPaymentHistory([]);
                    setHistoryInscriptions([]);
                }

                // 3. Fetch All Teams
                const teams = userData.athleteProfile?.teams || [];
                if (teams.length > 0) {
                    const teamPromises = teams.map(t =>
                        fetch(`${API_BASE_URL}/api/teams/${t.id}`, { headers: { 'Authorization': `Bearer ${token}` } })
                    );

                    const teamResponses = await Promise.all(teamPromises);
                    const fetchedTeamsData = await Promise.all(teamResponses.map(r => r.ok ? r.json() : null));

                    const validTeams = fetchedTeamsData.filter(t => t !== null);
                    setTeamsData(validTeams);

                    if (validTeams.length > 0 && !selectedTeamId) {
                        setSelectedTeamId(validTeams[0].id);
                    }
                } else {
                    setTeamsData([]);
                }

                // 4. Fetch Annual Passes
                try {
                    const annualResponse = await fetch(`${API_BASE_URL}/api/tickets/my-annual-passes`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (annualResponse.ok) {
                        const annualData = await annualResponse.json();
                        setAnnualPasses(annualData);
                    }
                } catch (aErr) {
                    console.error('Error fetching annual passes:', aErr);
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
                setLoadingTab(false);
            }
        };

        fetchData();
    }, [selectedUserId, historyYear, refreshKey]);

    // Fetch Notifications
    useEffect(() => {
        if (!selectedUserId) return;

        const fetchNotifications = async () => {
            setIsNotificationsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/api/notifications/user/${selectedUserId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setNotifications(data);
                }
            } catch (err) {
                console.error('Error fetching notifications:', err);
            } finally {
                setIsNotificationsLoading(false);
            }
        };

        fetchNotifications();
    }, [selectedUserId]);

    // Fetch events for specific team when selection changes
    useEffect(() => {
        if (!selectedTeamId || !selectedUserId) return;

        const fetchTeamEvents = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/api/events?teamId=${selectedTeamId}&startDate=${new Date().toISOString()}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    const teamEvents = await response.json();
                    setEvents(teamEvents
                        .sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime))
                        .slice(0, 6)
                    );
                } else {
                    setEvents([]);
                }
            } catch (err) {
                console.error('Error fetching team events:', err);
                setEvents([]);
            }
        };

        fetchTeamEvents();
    }, [selectedTeamId, selectedUserId]);

    const handleMarkAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/notifications/${notificationId}/read`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n));
            }
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    };

    const handleMarkAllAsRead = async () => {
        const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
        if (unreadIds.length === 0) return;

        try {
            const token = localStorage.getItem('token');
            // Assuming the API supports multiple or we loop
            await Promise.all(unreadIds.map(id =>
                fetch(`${API_BASE_URL}/api/notifications/${id}/read`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ));
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (err) {
            console.error('Error marking all as read:', err);
        }
    };

    const getNotificationCategory = (notif) => {
        const subject = notif.title?.toLowerCase() || '';
        const msg = notif.content?.toLowerCase() || '';

        if (subject.includes('pagamento') || subject.includes('quota') || msg.includes('pagamento')) {
            return { icon: 'fas fa-file-invoice-dollar', color: 'blue', label: 'Pagamento' };
        }
        if (subject.includes('treino') || subject.includes('horário') || subject.includes('convocatória')) {
            return { icon: 'fas fa-whistle', color: 'blue', label: 'Treino' };
        }
        if (subject.includes('jogo') || subject.includes('partida')) {
            return { icon: 'fas fa-basketball-ball', color: 'blue', label: 'Jogo' };
        }
        return { icon: 'fas fa-bell', color: 'blue', label: 'Aviso' };
    };



    if (loading) {
        const storedName = localStorage.getItem('userName');
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 15, 38, 0.9)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 99999,
                animation: 'fadeInOverlay 0.3s ease-out forwards'
            }}>
                <style>{`
                    @keyframes fadeInOverlay {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes pulseLogo {
                        0% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                        50% { transform: scale(1.05); opacity: 1; filter: drop-shadow(0 0 25px rgba(255,255,255,0.3)); }
                        100% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                    }
                    @keyframes rotateSpinner {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '24px',
                    textAlign: 'center',
                    padding: '32px',
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
                    maxWidth: '400px',
                    width: '90%'
                }}>
                    <div style={{ position: 'relative', width: '90px', height: '90px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <img
                            src="/CDP_logo.png"
                            alt="CDP Logo"
                            style={{
                                width: '64px',
                                height: '64px',
                                objectFit: 'contain',
                                animation: 'pulseLogo 2s ease-in-out infinite',
                                zIndex: 2
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            width: '84px',
                            height: '84px',
                            borderRadius: '50%',
                            border: '3px solid transparent',
                            borderTopColor: '#003380',
                            borderRightColor: '#0044ff',
                            animation: 'rotateSpinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
                            zIndex: 1
                        }}></div>
                    </div>
                    <div>
                        <h3 style={{
                            color: 'white',
                            margin: '0 0 8px 0',
                            fontSize: '1.25rem',
                            fontWeight: '700',
                            letterSpacing: '-0.025em'
                        }}>
                            A carregar...
                        </h3>
                        {storedName && (
                            <p style={{
                                color: '#94a3b8',
                                margin: 0,
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>
                                A entrar como <span style={{ color: '#60a5fa', fontWeight: '600' }}>{storedName}</span>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    if (error) return <div className="dashboard-error">Erro: {error}</div>;
    if (!athleteData) return <div className="dashboard-error">Não foi possível carregar os dados.</div>;

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('pt-PT');
    };


    const calculateAge = (birthDate) => {
        if (!birthDate) return 'N/A';
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const primaryTeamData = teamsData.find(t => t.id === selectedTeamId) || teamsData[0] || {};

    const handleSaveProfile = (updatedData) => {
        setAthleteData(prev => ({ ...prev, ...updatedData }));
        localStorage.setItem('userName', `${updatedData.firstName} ${updatedData.lastName}`);
    };

    const getDisplayEmail = (email) => {
        if (!email) return 'N/A';
        // Check for alias pattern: local+alias@domain
        const atIndex = email.lastIndexOf('@');
        if (atIndex === -1) return email;

        const localPart = email.substring(0, atIndex);
        const domain = email.substring(atIndex);

        // If local part has '+', strip everything after it
        const plusIndex = localPart.indexOf('+');
        if (plusIndex !== -1) {
            return localPart.substring(0, plusIndex) + domain;
        }
        return email;
    };

    const getSportIcon = (sport) => {
        const s = sport?.toLowerCase() || '';
        if (s.includes('basquetebol')) return 'fas fa-basketball-ball';
        if (s.includes('futebol')) return 'fas fa-futbol';
        if (s.includes('voleibol')) return 'fas fa-volleyball-ball';
        if (s.includes('natação') || s.includes('natacao')) return 'fas fa-swimmer';
        if (s.includes('andebol')) return 'fas fa-running';
        if (s.includes('ténis') || s.includes('tenis')) return 'fas fa-table-tennis';
        return 'fas fa-running';
    };

    const teamNames = (athleteData.athleteProfile?.teams || []).map(t => `${t.sportName} - ${t.name}${t.season ? ` (${t.season})` : ''}`).join(' / ') || 'Sem Equipa';
    const sportNames = (athleteData.athleteProfile?.teams || []).map(t => t.sportName).filter((v, i, a) => a.indexOf(v) === i).join(', ') || 'Sem Modalidade';

    const athleteTeamInfo = athleteData.athleteProfile?.teams?.find(t => t.id === selectedTeamId) || athleteData.athleteProfile?.teams?.[0] || {};
    const position = athleteTeamInfo.position || 'Atleta';
    const jerseyNumber = athleteTeamInfo.jerseyNumber;

    return (
        <div className="dashboard-wrapper">
            {isSwitchingUser && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 15, 38, 0.9)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 99999,
                    animation: 'fadeInOverlay 0.3s ease-out forwards'
                }}>
                    <style>{`
                        @keyframes fadeInOverlay {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        @keyframes pulseLogo {
                            0% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                            50% { transform: scale(1.05); opacity: 1; filter: drop-shadow(0 0 25px rgba(255,255,255,0.3)); }
                            100% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                        }
                        @keyframes rotateSpinner {
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '24px',
                        textAlign: 'center',
                        padding: '32px',
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
                        maxWidth: '400px',
                        width: '90%'
                    }}>
                        <div style={{ position: 'relative', width: '90px', height: '90px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img
                                src="/CDP_logo.png"
                                alt="CDP Logo"
                                style={{
                                    width: '64px',
                                    height: '64px',
                                    objectFit: 'contain',
                                    animation: 'pulseLogo 2s ease-in-out infinite',
                                    zIndex: 2
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                width: '84px',
                                height: '84px',
                                borderRadius: '50%',
                                border: '3px solid transparent',
                                borderTopColor: '#003380',
                                borderRightColor: '#0044ff',
                                animation: 'rotateSpinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
                                zIndex: 1
                            }}></div>
                        </div>
                        <div>
                            <h3 style={{
                                color: 'white',
                                margin: '0 0 8px 0',
                                fontSize: '1.25rem',
                                fontWeight: '700',
                                letterSpacing: '-0.025em'
                            }}>
                                A alterar perfil...
                            </h3>
                            {switchingToName && (
                                <p style={{
                                    color: '#94a3b8',
                                    margin: 0,
                                    fontSize: '0.95rem',
                                    fontWeight: '500'
                                }}>
                                    A entrar como <span style={{ color: '#60a5fa', fontWeight: '600' }}>{switchingToName}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="dashboard-bg-decoration">
                <div className="blob blob-1"></div>
                <div className="blob blob-2"></div>
                <div className="blob blob-3"></div>
            </div>

            {paymentToast && (
                <div className={`payment-toast ${paymentToast}`}>
                    <div className="toast-content">
                        <div className={`toast-icon ${paymentToast}`}>
                            <i className={paymentToast === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}></i>
                        </div>
                        <div className="toast-text">
                            <strong>
                                {paymentToast === 'success' ? 'Pagamento Concluído!' : 'Pagamento Interrompido'}
                            </strong>
                            <p>
                                {paymentToast === 'success'
                                    ? 'O processo foi iniciado com sucesso. Poderá demorar alguns minutos a atualizar no sistema.'
                                    : 'O processo de pagamento foi cancelado. Pode tentar novamente quando desejar.'}
                            </p>
                        </div>
                        <button className="toast-close" onClick={() => setPaymentToast(null)}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            )}
            {/* Athlete Tabs - shown when there are multiple linked users (siblings) */}
            {linkedUsers.length > 1 && (
                <div className="athlete-tabs">
                    <div className="container athlete-tabs-container">
                        {linkedUsers.map((lu) => (
                            <button
                                key={lu.id}
                                onClick={() => handleLinkedTabClick(lu)}
                                className={`athlete-tab ${selectedUserId === lu.id ? 'active' : ''} ${lu.requiresConsent ? 'is-blocked' : ''}`}
                                title={lu.requiresConsent ? 'Requer Autorização (18+ anos)' : lu.requiresPin ? 'Requer PIN (18+ anos)' : ''}
                            >
                                {lu.requiresConsent ? (
                                    <FaLock style={{ color: '#ef4444', marginRight: '6px' }} />
                                ) : lu.requiresPin ? (
                                    <FaLock style={{ color: '#eab308', marginRight: '6px' }} />
                                ) : null}
                                {`${lu.firstName} ${lu.lastName}`.trim() || `Atleta ${lu.id}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <header className="profile-hero">
                <div className="container">
                    <div className="hero-content-wrapper">
                        <div className="profile-main-info">
                            <div className="avatar-wrapper" onClick={() => setIsEditModalOpen(true)} style={{ cursor: 'pointer', position: 'relative' }} title="Alterar foto de perfil">
                                <img
                                    src={athleteData.profilePhotoUrl
                                        ? `${API_BASE_URL}${athleteData.profilePhotoUrl}`
                                        : "https://images.unsplash.com/vector-1742875355318-00d715aec3e8?q=80&w=880&auto=format&fit=crop"}
                                    alt={athleteData.fullName}
                                    className="profile-avatar-img"
                                    onError={(e) => { e.target.src = "https://images.unsplash.com/vector-1742875355318-00d715aec3e8?q=80&w=880&auto=format&fit=crop"; }}
                                />
                                <div className="avatar-edit-overlay">
                                    <i className="fas fa-camera"></i>
                                </div>
                            </div>
                            <div className="profile-text-info">
                                <h1 className="athlete-full-name">
                                    {athleteData.athleteProfile
                                        ? `${athleteData.athleteProfile.firstName || ''} ${athleteData.athleteProfile.lastName || ''}`.trim() || athleteData.fullName
                                        : athleteData.fullName
                                    }
                                </h1>
                                <div className="hero-meta-grid">
                                    <div className="hero-meta-item">
                                        <i className={getSportIcon(sportNames)}></i>
                                        <div className="sport-badges-container">
                                            <span>{sportNames}</span>
                                            {(() => {
                                                const athletesSports = athleteData.athleteProfile?.teams || [];
                                                return athletesSports.map(t => {
                                                    if (annualPasses.includes(t.sportId)) {
                                                        return (
                                                            <span key={t.id} className="annual-pass-badge" title="Passe Anual Ativo">
                                                                <i className="fas fa-id-badge"></i> {t.sportName} (ANUAL)
                                                            </span>
                                                        );
                                                    }
                                                    return null;
                                                });
                                            })()}
                                        </div>
                                    </div>
                                    <div className="hero-meta-item team-selector-wrapper">
                                        <i className="fas fa-users-viewfinder"></i>
                                        {teamsData.length > 1 ? (
                                            <div className="hero-team-select-container">
                                                <select
                                                    value={selectedTeamId}
                                                    onChange={(e) => setSelectedTeamId(parseInt(e.target.value))}
                                                    className="hero-team-dropdown"
                                                >
                                                    {teamsData.map(t => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.sportName} - {t.name}{t.season ? ` (${t.season})` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                                <i className="fas fa-chevron-down dropdown-arrow"></i>
                                            </div>
                                        ) : (
                                            <span>{teamNames}</span>
                                        )}
                                    </div>
                                    <div className="hero-meta-item">
                                        <i className="fas fa-id-card-clip"></i>
                                        <span>{position}</span>
                                    </div>
                                    {jerseyNumber && (
                                        <div className="hero-meta-item">
                                            <i className="fas fa-tshirt"></i>
                                            <span>Nº {jerseyNumber}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="hero-actions">
                            <button className="btn-hero-edit" onClick={() => setIsEditModalOpen(true)}>
                                <i className="fas fa-pen-nib"></i> Editar Perfil
                            </button>
                            <button
                                className="btn-hero-edit"
                                onClick={() => setIsPrivacyModalOpen(true)}
                                style={{ background: 'rgba(255, 255, 255, 0.18)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255, 255, 255, 0.3)' }}
                                title="Quem tem permissão para aceder à minha conta"
                            >
                                <FaShieldAlt style={{ marginRight: '6px' }} /> Permissões & Privacidade
                            </button>
                            {isAlsoTreinador && (
                                <button
                                    className="role-switch-btn"
                                    onClick={() => navigate('/dashboard-treinador')}
                                    title="Ver Dashboard Treinador"
                                >
                                    <i className="fas fa-chalkboard-teacher"></i>
                                    <span>Ver como Treinador</span>
                                </button>
                            )}
                            {isAlsoCoordenador && (
                                <button
                                    className="role-switch-btn"
                                    onClick={() => navigate('/dashboard-coordenador')}
                                    title="Ver Dashboard Coordenador"
                                >
                                    <i className="fas fa-users-cog"></i>
                                    <span>Ver como Coordenador</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <section className="dashboard-metrics">
                <div className="container">
                    <div className="metrics-grid">
                        <div className="metric-card glass-card">
                            <div className="metric-icon-box blue">
                                <i className="fas fa-trophy"></i>
                            </div>
                            <div className="metric-content">
                                <span className="metric-value">{athleteMetrics.gamesPlayed}</span>
                                <span className="metric-label">Jogos Realizados</span>
                            </div>
                        </div>

                        <div className="metric-card glass-card">
                            <div className="metric-icon-box green">
                                <i className="fas fa-calendar-check"></i>
                            </div>
                            <div className="metric-content">
                                <span className="metric-value">{athleteMetrics.attendanceRate !== '-' ? `${athleteMetrics.attendanceRate}%` : '-'}</span>
                                <span className="metric-label">Presença em Treinos</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section>
                <div className="container">
                    <div className="dashboard-content">
                        <div className="dashboard-main-column">
                            <div className="dashboard-card glass-card">
                                <div className="card-header">
                                    <div className="card-title">
                                        <div className="card-icon"><i className="far fa-calendar-alt"></i></div>
                                        <h2>Próximos Eventos</h2>
                                    </div>
                                    <button className="btn-text-action" onClick={() => setIsCalendarModalOpen(true)}>
                                        Ver Calendário <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                                <div className="events-timeline">
                                    {events.length > 0 ? (
                                        events.map(event => (
                                            <div key={event.id} className="timeline-event-item">
                                                <div className="event-date-badge">
                                                    <span className="day">{new Date(event.startDateTime).toLocaleDateString('pt-PT', { day: '2-digit' })}</span>
                                                    <span className="month">{new Date(event.startDateTime).toLocaleDateString('pt-PT', { month: 'short' }).replace('.', '')}</span>
                                                </div>
                                                <div className="event-info-box">
                                                    <div className="event-title-row">
                                                        <span className={`event-type-tag type-${event.eventType}`}>
                                                            {event.eventType === 1 ? 'Jogo' : event.eventType === 2 ? 'Treino' : 'Evento'}
                                                        </span>
                                                        <h4>{event.title}</h4>
                                                    </div>
                                                    <div className="event-meta-row">
                                                        <span className="meta-item"><i className="far fa-clock"></i> {new Date(event.startDateTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</span>
                                                        {event.location && <span className="meta-item"><i className="fas fa-location-dot"></i> {event.location}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                            Não existem eventos agendados.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="dashboard-card glass-card">
                                <div className="card-header">
                                    <div className="card-title">
                                        <div className="card-icon"><i className="fas fa-chart-bar"></i></div>
                                        <h2>Estatísticas da Equipa</h2>
                                    </div>
                                </div>

                                {teamReport ? (
                                    <div className="team-stats-summary">
                                        <div className="team-performance-row">
                                            <div className="performance-chart-mini">
                                                <div className="win-rate-circle" style={{ '--rate': `${teamReport.performance?.winRate || 0}%` }}>
                                                    <span className="rate-value">{teamReport.performance?.winRate}%</span>
                                                    <span className="rate-label">Vitórias</span>
                                                </div>
                                            </div>
                                            <div className="performance-stats-grid">
                                                <div className="p-stat-item">
                                                    <span className="p-stat-value">{teamReport.performance?.totalGames}</span>
                                                    <span className="p-stat-label">Jogos</span>
                                                </div>
                                                <div className="p-stat-item success">
                                                    <span className="p-stat-value">{teamReport.performance?.wins}</span>
                                                    <span className="p-stat-label">Vitórias</span>
                                                </div>
                                                <div className="p-stat-item danger">
                                                    <span className="p-stat-value">{teamReport.performance?.losses}</span>
                                                    <span className="p-stat-label">Derrotas</span>
                                                </div>
                                                {teamReport.performance?.draws > 0 && (
                                                    <div className="p-stat-item warning">
                                                        <span className="p-stat-value">{teamReport.performance?.draws}</span>
                                                        <span className="p-stat-label">Empates</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="scoring-summary-row">
                                            <div className="scoring-stat-box">
                                                <div className="scoring-label">
                                                    <i className="fas fa-arrow-up-right"></i>
                                                    <span>{getSportConfig(teamReport.sportName)?.label || 'Marcados'}</span>
                                                </div>
                                                <div className="scoring-value-group">
                                                    <span className="scoring-main-value">{teamReport.performance?.totalScored}</span>
                                                    <span className="scoring-avg">Avg: {(teamReport.performance?.totalScored / (teamReport.performance?.totalGames || 1)).toFixed(1)}</span>
                                                </div>
                                            </div>
                                            <div className="scoring-stat-box conceded">
                                                <div className="scoring-label">
                                                    <i className="fas fa-arrow-down-left"></i>
                                                    <span>Sofridos</span>
                                                </div>
                                                <div className="scoring-value-group">
                                                    <span className="scoring-main-value">{teamReport.performance?.totalConceded}</span>
                                                    <span className="scoring-avg">Avg: {(teamReport.performance?.totalConceded / (teamReport.performance?.totalGames || 1)).toFixed(1)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {teamReport.performance?.recentResults?.length > 0 && (
                                            <div className="recent-results-row">
                                                <span className="recent-label">Últimos Resultados:</span>
                                                <div className="results-sequence">
                                                    {teamReport.performance.recentResults.slice(-5).map((res, i) => (
                                                        <span key={i} className={`result-pill ${res.outcome.toLowerCase()}`} title={res.title}>
                                                            {res.outcome}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                                        <i className="fas fa-chart-pie" style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.3 }}></i>
                                        <p>Selecione uma equipa para ver as estatísticas.</p>
                                    </div>
                                )}
                            </div>

                            <div className="dashboard-card glass-card">
                                <div className="card-header">
                                    <div className="card-title">
                                        <div className="card-icon"><i className="fas fa-user-astronaut"></i></div>
                                        <h2>Minhas Estatísticas (Médias/Jogo)</h2>
                                    </div>
                                </div>

                                {teamReport && athleteData?.athleteProfile?.id ? (() => {
                                    const myStats = teamReport.athleteStats?.find(a => a.athleteId === athleteData.athleteProfile.id);
                                    const sportConfig = getSportConfig(teamReport.sportName);

                                    if (!myStats || !sportConfig) return (
                                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                                            Estatísticas individuais não disponíveis para esta equipa.
                                        </div>
                                    );

                                    const statsToShow = sportConfig.stats?.filter(s => s.inputType === 'number') || [];

                                    if (statsToShow.length === 0) return (
                                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                                            Não existem métricas configuradas para esta modalidade.
                                        </div>
                                    );

                                    return (
                                        <div className="athlete-stats-grid">
                                            {statsToShow.map(stat => (
                                                <div key={stat.key} className="athlete-stat-box">
                                                    <span className="a-stat-label">{stat.label}</span>
                                                    <div className="a-stat-value-row">
                                                        <span className="a-stat-value">{myStats.averages[stat.key] ?? 0}</span>
                                                        {stat.unit && <span className="a-stat-unit">{stat.unit}</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })() : (
                                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b' }}>
                                        <p>A carregar estatísticas individuais...</p>
                                    </div>
                                )}
                            </div>

                            <div className="dashboard-card glass-card">
                                <div className="card-header">
                                    <div className="card-title">
                                        <div className="card-icon"><i className="fas fa-user-shield"></i></div>
                                        <h2>Dados Pessoais</h2>
                                    </div>
                                    <button className="btn-circle-action" onClick={() => setIsEditModalOpen(true)}>
                                        <i className="fas fa-user-pen"></i>
                                    </button>
                                </div>

                                <div className="user-details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Data de Nascimento</span>
                                        <span className="detail-value">{formatDate(athleteData.birthDate)}</span>
                                    </div>

                                    <div className="detail-item">
                                        <span className="detail-label">Idade</span>
                                        <span className="detail-value">{calculateAge(athleteData.birthDate)} anos</span>
                                    </div>

                                    <div className="detail-item">
                                        <span className="detail-label">Número de Sócio</span>
                                        <span className="detail-value">{athleteData.memberProfile?.membershipNumber || 'N/A'}</span>
                                    </div>

                                    <div className="detail-item">
                                        <span className="detail-label">Data de Inscrição</span>
                                        <span className="detail-value">{formatDate(athleteData.memberProfile?.memberSince)}</span>
                                    </div>

                                    <div className="detail-item full-width">
                                        <span className="detail-label">Email Principal</span>
                                        <span className="detail-value">{getDisplayEmail(athleteData.email)}</span>
                                    </div>

                                    <div className="detail-item full-width">
                                        <span className="detail-label">Telemóvel</span>
                                        <span className="detail-value">{athleteData.phone || 'N/A'}</span>
                                    </div>

                                    <div className="detail-item full-width">
                                        <span className="detail-label">Endereço Residencial</span>
                                        <span className="detail-value">
                                            {athleteData.address ? `${athleteData.address}, ${athleteData.postalCode} ${athleteData.city}` : 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="dashboard-card glass-card">
                                <div className="card-header">
                                    <div className="card-title">
                                        <div className="card-icon"><i className="fas fa-people-group"></i></div>
                                        <h2>A Minha Equipa</h2>
                                    </div>
                                    <div className="team-controls">
                                        <button className="btn-circle-action" onClick={() => setIsTeamModalOpen(true)}>
                                            <i className="fas fa-expand"></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="team-grid">
                                    {(() => {
                                        const currentTeam = teamsData.find(t => t.id === selectedTeamId) || teamsData[0];
                                        if (!currentTeam) return (
                                            <div style={{ padding: '20px', textAlign: 'center', gridColumn: '1/-1', color: '#666' }}>
                                                Ainda não tens equipa atribuída.
                                            </div>
                                        );

                                        return (
                                            <>
                                                {currentTeam.coaches?.slice(0, 2).map((coach) => (
                                                    <div className="team-member" key={`coach-${coach.id}`}>
                                                        <div className="team-member-avatar coach">
                                                            {coach.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                                        </div>
                                                        <div className="team-member-info">
                                                            <h4>{coach.name}</h4>
                                                            <span className="team-member-role">Treinador</span>
                                                        </div>
                                                    </div>
                                                ))}

                                                {currentTeam.athletes?.slice(0, 4).map((athlete) => (
                                                    <div className="team-member" key={`athlete-${athlete.id}`}>
                                                        <div className="team-member-avatar">
                                                            {athlete.jerseyNumber || athlete.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                                        </div>
                                                        <div className="team-member-info">
                                                            <h4>{athlete.name}</h4>
                                                            <span className={`team-member-role ${athlete.isCaptain ? 'captain' : ''}`}>
                                                                {athlete.position || 'Atleta'} {athlete.isCaptain && '• Capitão'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        <aside className="dashboard-side-column">
                            <div>
                                <PaymentCard
                                    paymentStatus={paymentStatus}
                                    quotaAmount={quotaAmount}
                                    totalDue={totalDue}
                                    overdueMonths={overdueMonths}
                                    futureMonths={futureMonths}
                                    paymentPreference={paymentPreference}
                                    paymentReference={paymentReference}
                                    breakdown={quotaBreakdown}
                                    discountsApplied={discountsApplied}
                                    inscriptionInfo={inscriptionInfo}
                                    nextPeriod={nextPeriod}
                                    paymentHistory={paymentHistory}
                                    historyInscriptions={historyInscriptions}
                                    historyYear={historyYear}
                                    onHistoryYearChange={handleHistoryYearChange}
                                    onGenerateReference={handleStartPayment}
                                    generatingReference={startingPayment}
                                    onStartPayment={handleStartPayment}
                                    startingPayment={startingPayment}
                                    parentsPaymentWarning={parentsPaymentWarning}
                                    isPollingPayment={isPollingPayment}
                                />

                                <div className="dashboard-card glass-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <div className="card-icon"><i className="fas fa-bullhorn"></i></div>
                                            <h2>Notificações</h2>
                                        </div>
                                        <div className="nm-header-actions" style={{ display: 'flex', gap: '8px' }}>
                                            {notifications.some(n => !n.isRead) && (
                                                <button className="btn-mark-all" onClick={handleMarkAllAsRead} title="Marcar todas como lidas">
                                                    <i className="fas fa-check-double"></i>
                                                </button>
                                            )}
                                            <button className="btn-mark-all" onClick={() => setIsAllNotificationsModalOpen(true)} title="Ver todas as notificações">
                                                <i className="fas fa-external-link-alt"></i>
                                            </button>
                                        </div>

                                    </div>

                                    <div className="notifications-list">
                                        {notifications.length > 0 ? (
                                            notifications.slice(0, 2).map((notif) => {

                                                const category = getNotificationCategory(notif);
                                                const isExpanded = expandedNotificationId === notif.id;

                                                return (
                                                    <div
                                                        key={notif.id}
                                                        className={`notification-item ${!notif.isRead ? 'is-unread' : ''} ${isExpanded ? 'is-expanded' : ''} category-${category.color}`}
                                                        onClick={() => {
                                                            if (!notif.isRead) handleMarkAsRead(notif.id);
                                                            setExpandedNotificationId(isExpanded ? null : notif.id);
                                                        }}
                                                    >
                                                        <div className="notification-side-indicator"></div>
                                                        <div className="notification-content">
                                                            <div className="notification-meta">
                                                                <span className="n-date">{formatDate(notif.createdAt)}</span>
                                                            </div>
                                                            <h4 className="notification-title">{notif.title}</h4>
                                                            <p className="notification-message">
                                                                {notif.content}
                                                            </p>
                                                            <div className="notification-footer">
                                                                <span className="n-sender">Por: {notif.senderName || 'Sistema'}</span>
                                                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} expand-icon`}></i>
                                                            </div>
                                                        </div>

                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="notifications-placeholder">
                                                <div className="placeholder-icon">
                                                    <i className="fas fa-bell-slash"></i>
                                                </div>
                                                <p>Não existem notificações recentes.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>


                                <div className="dashboard-card glass-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <div className="card-icon"><i className="fas fa-bolt-lightning"></i></div>
                                            <h2>Ações Rápidas</h2>
                                        </div>
                                    </div>

                                    <div className="quick-actions-grid">
                                        <button className="q-action-item" onClick={() => setIsCalendarModalOpen(true)}>
                                            <div className="q-icon"><i className="fas fa-calendar-days"></i></div>
                                            <span>Calendário</span>
                                        </button>
                                        <button className="q-action-item" onClick={() => setIsEditModalOpen(true)}>
                                            <div className="q-icon"><i className="fas fa-user-gear"></i></div>
                                            <span>Editar Perfil</span>
                                        </button>
                                        <button className="q-action-item">
                                            <div className="q-icon"><i className="fas fa-file-waveform"></i></div>
                                            <span>Ficha Médica</span>
                                        </button>
                                        <button className="q-action-item" onClick={() => setIsRequestsModalOpen(true)}>
                                            <div className="q-icon"><i className="fas fa-clipboard-check"></i></div>
                                            <span>Requisições</span>
                                        </button>
                                        <button className="q-action-item" onClick={() => setIsTicketsModalOpen(true)}>
                                            <div className="q-icon" style={{ background: '#003380', color: 'white' }}><i className="fas fa-ticket-alt"></i></div>
                                            <span>Os Meus Bilhetes</span>
                                        </button>
                                        <button className="q-action-item" onClick={() => setIsReceiptsModalOpen(true)}>
                                            <div className="q-icon"><i className="fas fa-file-invoice"></i></div>
                                            <span>Recibos</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Membership Card */}
                                <MembershipCard
                                    memberNumber={athleteData.memberProfile?.membershipNumber || athleteData.id?.toString()}
                                    name={
                                        athleteData.athleteProfile
                                            ? `${athleteData.athleteProfile.firstName || ''} ${athleteData.athleteProfile.lastName || ''}`.trim() || athleteData.fullName
                                            : athleteData.fullName
                                    }
                                    memberSince={athleteData.memberProfile?.memberSince ? new Date(athleteData.memberProfile.memberSince).getFullYear().toString() : '—'}
                                    status={athleteData.memberProfile?.membershipStatus !== undefined
                                        ? ['Pending', 'Active', 'Suspended', 'Cancelled'][athleteData.memberProfile.membershipStatus] || 'Ativo'
                                        : 'Ativo'}
                                    sport={athleteData.sport}
                                    cardType="atleta"
                                    userId={selectedUserId}
                                    compact={true}
                                    photoUrl={athleteData.profilePhotoUrl}
                                    paymentState={getCardPaymentState(paymentStatus, overdueMonths)}
                                />

                                <div className="dashboard-card glass-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <div className="card-icon"><i className="fas fa-file-zipper"></i></div>
                                            <h2>Documentos</h2>
                                        </div>
                                        <button className="btn-text-action" onClick={() => setIsDocumentsModalOpen(true)}>
                                            Ver Todos <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>

                                    <div className="documents-display-list">
                                        <div className="doc-list-item">
                                            <div className="doc-main">
                                                <div className="doc-icon pdf"><i className="fas fa-file-pdf"></i></div>
                                                <div className="doc-info">
                                                    <span className="doc-name">Cartão de Sócio</span>
                                                    <span className="doc-meta">PDF • 156 KB</span>
                                                </div>
                                            </div>
                                            <button
                                                className="btn-doc-download"
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    const token = localStorage.getItem('token');
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/api/membershipcard/download?userId=${selectedUserId}`, {
                                                            headers: { 'Authorization': `Bearer ${token}` }
                                                        });
                                                        if (response.ok) {
                                                            const blob = await response.blob();
                                                            const url = window.URL.createObjectURL(blob);
                                                            const a = document.createElement('a');
                                                            a.href = url;
                                                            a.download = `Cartao_Socio_${selectedUserId}.pdf`;
                                                            document.body.appendChild(a);
                                                            a.click();
                                                            a.remove();
                                                        }
                                                    } catch (err) {
                                                        console.error('Download failed', err);
                                                    }
                                                }}
                                            >
                                                <i className="fas fa-cloud-arrow-down"></i>
                                            </button>
                                        </div>
                                        <div className="doc-list-item" style={{ borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '15px' }}>
                                            <div className="doc-main">
                                                <div className="doc-icon pdf" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}><i className="fas fa-book"></i></div>
                                                <div className="doc-info">
                                                    <span className="doc-name">Manual do Atleta</span>
                                                    <span className="doc-meta">PDF • 5.1 MB</span>
                                                </div>
                                            </div>
                                            <a
                                                href="/manual-atleta.pdf"
                                                target="_blank"
                                                download="Manual_Atleta.pdf"
                                                className="btn-doc-download"
                                                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <i className="fas fa-cloud-arrow-down"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div >
            </section >

            <EditProfileModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                userData={athleteData}
                onSave={handleSaveProfile}
            />
            <ReceiptsModal
                isOpen={isReceiptsModalOpen}
                onClose={() => setIsReceiptsModalOpen(false)}
                userId={selectedUserId}
            />

            <TeamDetailsModal
                isOpen={isTeamModalOpen}
                onClose={() => setIsTeamModalOpen(false)}
                teamData={teamsData.find(t => t.id === selectedTeamId) || teamsData[0]}
            />

            <CalendarModal
                isOpen={isCalendarModalOpen}
                onClose={() => setIsCalendarModalOpen(false)}
                teamId={selectedTeamId}
                sportName={primaryTeamData.sportName}
                canManageAttendance={false}
            />

            <RequestsModal
                isOpen={isRequestsModalOpen}
                onClose={() => setIsRequestsModalOpen(false)}
                userId={selectedUserId}
                athleteProfileEscalao={athleteData?.athleteProfile?.escalao}
            />

            <DocumentsModal
                isOpen={isDocumentsModalOpen}
                onClose={() => setIsDocumentsModalOpen(false)}
                userId={selectedUserId}
                userType="atleta"
            />

            <NotificationsModal
                isOpen={isAllNotificationsModalOpen}
                onClose={() => setIsAllNotificationsModalOpen(false)}
                notifications={notifications}
                onMarkAsRead={handleMarkAsRead}
                formatDate={formatDate}
            />

            <TicketsModal
                isOpen={isTicketsModalOpen}
                onClose={() => setIsTicketsModalOpen(false)}
                userId={selectedUserId}
            />

            {selectedGameForDetail && (
                <GameDetailModal
                    isOpen={isGameDetailOpen}
                    onClose={() => {
                        setIsGameDetailOpen(false);
                        setSelectedGameForDetail(null);
                    }}
                    game={selectedGameForDetail}
                    sportName={primaryTeamData.sportName}
                    readOnly={true}
                />
            )}

            <AdminPinModal
                isOpen={showAdminPinModal}
                targetUserId={pendingSwitchUser?.id}
                onSuccess={() => {
                    setShowAdminPinModal(false);
                    if (pendingSwitchUser) executeUserSwitch(pendingSwitchUser);
                }}
                onClose={() => setShowAdminPinModal(false)}
            />

            <ConsentRequestModal
                isOpen={showConsentModal}
                targetUser={targetConsentUser}
                onClose={() => setShowConsentModal(false)}
                onRequestSent={() => refreshLinkedUsers()}
            />

            <FamilyPrivacyModal
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
            />
        </div >
    );
};

export default DashboardAtleta;
